import { useNavigate } from "react-router-dom";
import { useCinematic } from "@/data/cinematicMode/context";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Film, Play, X, Settings2, FolderOpen } from "lucide-react";
import { SceneEditorPanel } from "@/components/cinematic/SceneEditorPanel";
import { DemoPresetsPanel } from "@/components/cinematic/DemoPresetsPanel";

export default function DemoLab() {
  const { state, startCinematic, stopCinematic, scenes } = useCinematic();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const navigate = useNavigate();

  const canEdit = hasPerm("admin_edit");

  const handleLaunch = () => {
    startCinematic();
    navigate(scenes[0].route);
  };

  return (
    <div className="max-w-4xl mx-auto py-12 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
          <Film className="h-3.5 w-3.5" />
          Cinematic Live Mode
        </div>
        <h1 className="text-2xl font-bold text-foreground">Koredor System Demo</h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Experience the full platform through a directed, cinematic walkthrough.
          Every screen is real. Every interaction is live.
        </p>
        {state.active && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { stopCinematic(); navigate("/"); }}>
            <X className="h-3.5 w-3.5" /> Exit Cinematic Mode
          </Button>
        )}
      </div>

      <Tabs defaultValue="preview" className="space-y-6">
        <TabsList className="mx-auto flex w-fit">
          <TabsTrigger value="preview" className="gap-1.5 text-xs">
            <Play className="h-3 w-3" /> Scenes
          </TabsTrigger>
          {canEdit && (
            <>
              <TabsTrigger value="editor" className="gap-1.5 text-xs">
                <Settings2 className="h-3 w-3" /> Scene Editor
              </TabsTrigger>
              <TabsTrigger value="presets" className="gap-1.5 text-xs">
                <FolderOpen className="h-3 w-3" /> Saved Demos
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Scene Preview Tab */}
        <TabsContent value="preview" className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-1">
              {scenes.length} Scenes
            </h2>
            <div className="grid gap-2">
              {scenes.map((scene, i) => (
                <div
                  key={scene.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors"
                >
                  <Badge variant="outline" className="text-[10px] font-mono h-5 w-5 flex items-center justify-center p-0 shrink-0">
                    {i + 1}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{scene.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{scene.subtitle}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground/60 font-mono shrink-0">{scene.route}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Button size="lg" className="gap-2" onClick={handleLaunch}>
              <Play className="h-4 w-4" />
              Launch Cinematic Demo
            </Button>
          </div>
        </TabsContent>

        {/* Scene Editor Tab (role-gated) */}
        {canEdit && (
          <TabsContent value="editor">
            <SceneEditorPanel />
          </TabsContent>
        )}

        {/* Saved Demos Tab (role-gated) */}
        {canEdit && (
          <TabsContent value="presets">
            <DemoPresetsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
